/**
 * Network connectivity test utilities
 */

export const testApiConnection = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  try {
    // Test the /me endpoint (should return 401 if not authenticated, which means API is working)
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
    const response = await fetch(`${apiUrl}/me`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 401) {
      return {
        success: true,
        message:
          'API connection successful (authentication required as expected)',
        details: {
          status: response.status,
          message: 'Authorization header missing - this is expected',
        },
      };
    } else if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        message: 'API connection successful (already authenticated)',
        details: data,
      };
    } else {
      const errorText = await response.text();
      return {
        success: false,
        message: `API responded with status: ${response.status}`,
        details: {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        },
      };
    }
  } catch (error) {
    return {
      success: false,
      message: 'Network connection failed',
      details: error,
    };
  }
};

export const testOpenApiDocs = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  try {
    // Test the OpenAPI docs endpoint (should be publicly accessible)
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
    const response = await fetch(`${apiUrl}/docs/openapi.json`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        message: 'OpenAPI docs accessible',
        details: { title: data.info?.title, version: data.info?.version },
      };
    } else {
      return {
        success: false,
        message: `OpenAPI docs failed with status: ${response.status}`,
        details: { status: response.status, statusText: response.statusText },
      };
    }
  } catch (error) {
    return {
      success: false,
      message: 'OpenAPI docs request failed',
      details: error,
    };
  }
};

export const testLogin = async (
  email: string,
  password: string
): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
    const response = await fetch(`${apiUrl}/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (response.ok) {
      const data = await response.text(); // Login endpoint returns text
      return {
        success: true,
        message: 'Login successful',
        details: data,
      };
    } else {
      const errorData = await response.text();
      return {
        success: false,
        message: `Login failed with status: ${response.status}`,
        details: { status: response.status, error: errorData },
      };
    }
  } catch (error) {
    return {
      success: false,
      message: 'Login request failed',
      details: error,
    };
  }
};
